import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A pike map, also known as a spike or prism map, is a data visualization technique that
          uses vertical spikes or columns to represent quantitative data on a geographic map. The
          height of each spike corresponds to the value of the data for that location, making it
          easy to compare magnitudes across regions. Pike maps are particularly effective for
          highlighting spatial distributions, trends, or outliers in metrics like population
          density, sales, or resource allocation.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Map-Pike charts provide a unique way to represent spatial data with vertical bars, making
          them particularly effective for certain applications:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visualizing Intensity Across Locations:</strong> Map-pike charts use vertical
            bars to indicate the magnitude of data values at specific locations, making them ideal
            for showcasing the intensity or volume of an attribute geographically.
          </li>
          <li>
            <strong>Comparing Regional Variations:</strong> By varying the height of the pikes,
            these charts effectively highlight differences between regions, such as population
            density, sales performance, or resource allocation.
          </li>
          <li>
            <strong>Identifying Patterns in Spatial Data:</strong> Map-pike charts help uncover
            spatial patterns or anomalies, providing insights into trends or imbalances across a
            geographic area.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Map-Pike charts can be a powerful visualization tool for geographical data, but they come
          with certain limitations that should be taken into account:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Clarity with Many Data Points:</strong> When displaying a large number
            of pikes on a map, the chart can become overcrowded, making it difficult to discern
            individual values and trends, especially in areas with dense data points.
          </li>
          <li>
            <strong>Misleading Interpretation of Data Size:</strong> Pike height is visually related
            to the data values, but in some cases, variations in height may be hard to accurately
            compare, leading to potential misinterpretation of the data&apos;s significance or
            scale.
          </li>
          <li>
            <strong>Geographic Overlap Issues:</strong> In regions with closely spaced data points,
            the pikes may overlap or cluster too tightly, diminishing the ability to see the exact
            distribution of data and potentially causing confusion in understanding geographic
            trends.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection plays a vital role in Map - Pike charts to ensure clarity and highlight
          key information. A sequential color palette is often used, with lighter shades
          representing lower values and darker shades indicating higher values, creating a clear
          visual distinction. In cases where data has a central or neutral point, a diverging color
          scheme can be used to emphasize deviations from that point, making it easier to interpret
          variations across the chart. Thoughtful use of color can help in effectively communicating
          spatial trends and values.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Map-Pike charts offer a dynamic way to represent data geographically, but they also
          present unique challenges that need to be addressed:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Visual Clutter in Dense Areas:</strong> When there are many data points in a
            small geographic area, the overlapping pikes can create visual clutter, making the map
            hard to interpret.
          </li>
          <li>
            <strong>Height Perception Issues:</strong> Accurately comparing the height of pikes can
            be challenging, especially if the map viewer lacks a consistent frame of reference or if
            3D effects distort perception.
          </li>
          <li>
            <strong>Geographic Obstruction:</strong> Tall or dense pikes may obscure underlying map
            details, limiting the viewer&apos;s ability to contextualize data within the
            geographical landscape.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
